import { Box, Button, Flex, Grid, Input, Text, useToast } from "@chakra-ui/react";
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import {
  FaBoxes,
  FaCheckCircle,
  FaClipboardList,
  FaClock,
  FaExclamationTriangle,
  FaFileInvoiceDollar,
  FaPlaneArrival,
  FaTruckLoading,
} from "react-icons/fa";
import { useMediaQuery } from "react-responsive";
import { useNavigate } from "react-router-dom";
import Layout from "../../../components/Layout";
import { allowedCompanyValues, companyOptions, useCompany } from "../../../contexts/CompanyContext";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import {
  AgendaConsult,
  AgendaRange,
  ManagementOperations,
  OpsAgendaItem,
  OpsPendingItem,
  OpsPortfolioItem,
  OpsQueryItem,
  PendingType,
} from "../../../models/GestaoOperations";
import { getOperations } from "../../../services/gestaoOperationsService";
import { crm, Pill, ResponsibleChip } from "../../../theme";
import {
  Card,
  Column,
  DataTable,
  EmptyState,
  ErrorState,
  fmtBrl,
  fmtDate,
  fmtMoney,
  fmtShortDate,
  fmtUsd,
  KpiTile,
  Loading,
  openProcessPath,
  PageHeader,
  ProcessCell,
  SegmentedTabs,
  SelectFilter,
  filterInputProps,
} from "./ui";

type Tab = "resumo" | "agenda" | "carteira" | "pendencias" | "consulta";

const pendingLabels: Record<PendingType, string> = {
  desembaraco: "Desembaraço atrasado",
  faturamento: "Faturamento atrasado",
  cambio: "Câmbio em aberto",
  followup: "Pendência do follow-up",
  dados: "Dados faltantes",
};

const pendingColors: Record<PendingType, { bg: string; fg: string }> = {
  desembaraco: { bg: "#fef2f2", fg: crm.danger },
  faturamento: { bg: "#fff7ed", fg: "#c2410c" },
  cambio: { bg: "#fffbeb", fg: crm.warning },
  followup: { bg: crm.brandSoft, fg: crm.brand },
  dados: { bg: crm.subtle, fg: crm.textMuted },
};

const cambioLabels = {
  pago: { text: "Pago", bg: "#f0fdf4", fg: crm.success },
  aberto: { text: "Em aberto", bg: "#fffbeb", fg: crm.warning },
  naoinformado: { text: "Não informado", bg: crm.subtle, fg: crm.textMuted },
};

const Operacoes = () => {
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
  const navigate = useNavigate();
  const toast = useToast();
  const { currentCompany, currentEmulation } = useCompany();
  const auth = useAuthUser<any>();
  const allowedUnits = allowedCompanyValues(auth?.company);

  const [tab, setTab] = useState<Tab>("resumo");
  const [unit, setUnit] = useState<string>(currentCompany && currentCompany !== 5 ? String(currentCompany) : "");
  const [searchText, setSearchText] = useState("");
  const [search, setSearch] = useState("");
  const [clientId, setClientId] = useState("");
  const [responsible, setResponsible] = useState("");
  const [stageId, setStageId] = useState("");
  const [status, setStatus] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [agendaRange, setAgendaRange] = useState<AgendaRange>("semana");
  const [agendaConsult, setAgendaConsult] = useState<AgendaConsult | "">("");
  const [operation, setOperation] = useState("");
  const [cambio, setCambio] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const [data, setData] = useState<ManagementOperations | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    setUnit(currentCompany && currentCompany !== 5 ? String(currentCompany) : "");
  }, [currentCompany]);

  // Busca com debounce.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchText.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchText]);

  const load = async () => {
    const current = ++seq.current;
    setLoading(true);
    setError(false);
    try {
      const result = await getOperations({
        type: unit ? Number(unit) : undefined,
        companyId: currentEmulation,
        search,
        clientId,
        responsible,
        stageId,
        status,
        agendaRange,
        agendaConsult,
        operation,
        cambio,
        page,
        pageSize,
      });
      if (current !== seq.current) return;
      setData(result);
    } catch (e) {
      if (current !== seq.current) return;
      console.log("error", e);
      setError(true);
      toast({ title: "Não foi possível carregar a Central de Operações.", status: "error" });
    } finally {
      if (current === seq.current) setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [unit, search, clientId, responsible, stageId, status, agendaRange, agendaConsult, operation, cambio, page, pageSize, currentEmulation]);

  const resetPageAnd = <T,>(setter: (v: T) => void) => (v: T) => {
    setter(v);
    setPage(1);
  };

  const advancedCount = [clientId, responsible, stageId, status].filter(Boolean).length;
  const open = (processId: string) => navigate(openProcessPath(processId));

  const filters = data?.filters;

  return (
    <Box height={isMobile ? undefined : "100vh"} overflow="auto">
      <Layout>
        <Box p="24px" bg={crm.pageBg} minH="100%">
          <PageHeader title="Central de Operações" subtitle="Acompanhamento diário da operação: prazos, carteira, pendências e consulta." />

          <Card p="16px">
            <Flex gap="12px" flexWrap="wrap" align="flex-end">
              <Box flex="1" minW="240px">
                <Text fontSize="11px" fontWeight="600" color={crm.textMuted} mb="3px" textTransform="uppercase">
                  Busca
                </Text>
                <Input
                  {...filterInputProps}
                  placeholder="Referência, cliente, DI, container ou BL"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </Box>
              <SelectFilter
                label="Unidade"
                allLabel="Todas"
                value={unit}
                onChange={resetPageAnd(setUnit)}
                options={companyOptions
                  .filter((c) => c.value !== 5 && (allowedUnits == null || allowedUnits.includes(c.value)))
                  .map((c) => ({ value: String(c.value), label: c.label }))}
              />
              <Button
                size="sm"
                variant="outline"
                borderRadius="10px"
                borderColor={advancedCount > 0 ? crm.brand : crm.border}
                color={advancedCount > 0 ? crm.brand : crm.text}
                _hover={{ bg: crm.brandSoft }}
                onClick={() => setShowAdvanced((v) => !v)}
              >
                Filtros avançados{advancedCount > 0 ? ` (${advancedCount})` : ""}
              </Button>
              {advancedCount > 0 && (
                <Button
                  size="sm"
                  variant="ghost"
                  color={crm.textMuted}
                  onClick={() => {
                    setClientId("");
                    setResponsible("");
                    setStageId("");
                    setStatus("");
                    setPage(1);
                  }}
                >
                  Limpar
                </Button>
              )}
            </Flex>
            {showAdvanced && (
              <Flex gap="12px" flexWrap="wrap" mt="14px" pt="14px" borderTop={`1px solid ${crm.border}`}>
                <SelectFilter
                  label="Cliente"
                  value={clientId}
                  onChange={resetPageAnd(setClientId)}
                  options={(filters?.clients ?? []).map((c) => ({ value: c.id ?? "", label: c.name }))}
                />
                <SelectFilter
                  label="Responsável"
                  value={responsible}
                  onChange={resetPageAnd(setResponsible)}
                  options={(filters?.responsibles ?? []).map((r) => ({ value: r, label: r }))}
                />
                <SelectFilter
                  label="Etapa"
                  value={stageId}
                  onChange={resetPageAnd(setStageId)}
                  options={(filters?.stages ?? []).map((s) => ({ value: s.id ?? "none", label: s.name }))}
                />
                <SelectFilter
                  label="Situação"
                  value={status}
                  onChange={resetPageAnd(setStatus)}
                  options={(filters?.statuses ?? []).map((s) => ({ value: s, label: s }))}
                />
              </Flex>
            )}
          </Card>

          <Box my="16px" overflowX="auto">
            <SegmentedTabs<Tab>
              value={tab}
              onChange={setTab}
              items={[
                { key: "resumo", label: "Resumo" },
                { key: "agenda", label: "Agenda" },
                { key: "carteira", label: "Carteira", count: data?.portfolio.kpis.processos },
                { key: "pendencias", label: "Pendências", count: data?.pendings.items.length },
                { key: "consulta", label: "Consulta", count: data?.query.total },
              ]}
            />
          </Box>

          {!data && loading && <Loading />}
          {!data && !loading && error && <ErrorState onRetry={load} />}

          {data && (
            <Box opacity={loading ? 0.6 : 1} transition="opacity .15s" pointerEvents={loading ? "none" : undefined}>
              {tab === "resumo" && <ResumoTab data={data} isMobile={isMobile} goTo={setTab} />}
              {tab === "agenda" && (
                <AgendaTab
                  data={data}
                  isMobile={isMobile}
                  range={agendaRange}
                  onRange={setAgendaRange}
                  consult={agendaConsult}
                  onConsult={setAgendaConsult}
                  open={open}
                />
              )}
              {tab === "carteira" && (
                <CarteiraTab
                  data={data}
                  isMobile={isMobile}
                  operation={operation}
                  onOperation={setOperation}
                  cambio={cambio}
                  onCambio={setCambio}
                  open={open}
                />
              )}
              {tab === "pendencias" && <PendenciasTab data={data} open={open} />}
              {tab === "consulta" && (
                <ConsultaTab
                  data={data}
                  open={open}
                  page={page}
                  pageSize={pageSize}
                  onPage={setPage}
                  onPageSize={(s) => {
                    setPageSize(s);
                    setPage(1);
                  }}
                />
              )}
            </Box>
          )}
        </Box>
      </Layout>
    </Box>
  );
};

// ------------------------------------------------------------------ Resumo

const ResumoTab = ({ data, isMobile, goTo }: { data: ManagementOperations; isMobile: boolean; goTo: (t: Tab) => void }) => {
  const s = data.summary;
  const cols = isMobile ? "1fr" : "repeat(4, 1fr)";
  return (
    <>
      <Grid templateColumns={cols} gap="16px" mb="16px">
        <KpiTile title="Processos (seleção)" value={s.processos} icon={<FaBoxes />} />
        <KpiTile title="Em curso" value={s.emCurso} icon={<FaTruckLoading />} />
        <KpiTile title="Finalizados" value={s.finalizados} hint="Inclui cancelados" icon={<FaCheckCircle />} tone="success" />
        <KpiTile
          title="Pendências do follow-up"
          value={s.pendenciasFollowUp}
          icon={<FaClipboardList />}
          tone={s.pendenciasFollowUp > 0 ? "warning" : "default"}
          onClick={() => goTo("pendencias")}
        />
      </Grid>

      <Grid templateColumns={isMobile ? "1fr" : "repeat(2, 1fr)"} gap="16px" mb="16px">
        <Card title="Prioridades operacionais">
          <Grid templateColumns="repeat(3, 1fr)" gap="12px">
            <MiniStat label="Desembaraços atrasados" value={s.prioridades.desembaracosAtrasados} danger onClick={() => goTo("pendencias")} />
            <MiniStat label="Faturamentos atrasados" value={s.prioridades.faturamentosAtrasados} danger onClick={() => goTo("pendencias")} />
            <MiniStat label="Pendências informadas" value={s.prioridades.pendenciasInformadas} onClick={() => goTo("pendencias")} />
          </Grid>
        </Card>
        <Card title={`Semana vigente · ${fmtShortDate(data.weekStart)} a ${fmtShortDate(data.weekEnd)}`}>
          <Grid templateColumns="repeat(3, 1fr)" gap="12px">
            <MiniStat label="Chegadas" value={s.semanaVigente.chegadas} icon={<FaPlaneArrival />} onClick={() => goTo("agenda")} />
            <MiniStat label="Desembaraços previstos" value={s.semanaVigente.desembaracosPrevistos} icon={<FaClock />} onClick={() => goTo("agenda")} />
            <MiniStat label="Faturamentos estimados" value={s.semanaVigente.faturamentosEstimados} icon={<FaFileInvoiceDollar />} onClick={() => goTo("agenda")} />
          </Grid>
        </Card>
      </Grid>

      <Card title="Alertas de parametrização · processos ainda não desembaraçados">
        <Grid templateColumns="repeat(3, 1fr)" gap="12px">
          <ChannelStat label="Canal vermelho" value={s.alertas.vermelho} color="#dc2626" />
          <ChannelStat label="Canal amarelo" value={s.alertas.amarelo} color="#eab308" />
          <ChannelStat label="Canal cinza" value={s.alertas.cinza} color="#94a3b8" />
        </Grid>
      </Card>
    </>
  );
};

const MiniStat = ({
  label,
  value,
  danger,
  icon,
  onClick,
}: {
  label: string;
  value: number;
  danger?: boolean;
  icon?: ReactNode;
  onClick?: () => void;
}) => (
  <Box
    p="14px"
    borderRadius="12px"
    bg={danger && value > 0 ? "#fef2f2" : crm.subtle}
    border={`1px solid ${danger && value > 0 ? "#fecaca" : crm.border}`}
    cursor={onClick ? "pointer" : undefined}
    _hover={onClick ? { borderColor: crm.brand } : undefined}
    onClick={onClick}
  >
    <Flex align="center" gap="6px" color={crm.textMuted} mb="6px">
      {icon}
      <Text fontSize="12px" fontWeight="500">
        {label}
      </Text>
    </Flex>
    <Text fontSize="24px" fontWeight="700" color={danger && value > 0 ? crm.danger : crm.text} lineHeight="1">
      {value}
    </Text>
  </Box>
);

const ChannelStat = ({ label, value, color }: { label: string; value: number; color: string }) => (
  <Flex align="center" gap="12px" p="14px" borderRadius="12px" bg={crm.subtle} border={`1px solid ${crm.border}`}>
    <Box w="12px" h="12px" borderRadius="full" bg={color} flexShrink={0} />
    <Text fontSize="13px" color={crm.textMuted} flex="1">
      {label}
    </Text>
    <Text fontSize="22px" fontWeight="700" color={crm.text}>
      {value}
    </Text>
  </Flex>
);

// ------------------------------------------------------------------ Agenda

const agendaColumns = (dateLabel: string, daysLabel?: string): Column<OpsAgendaItem>[] => [
  { header: "Processo / cliente", render: (r) => <ProcessCell ref_={r.ref} client={r.clientName} /> },
  { header: "Etapa", render: (r) => r.stageName ?? r.status ?? "—" },
  {
    header: dateLabel,
    render: (r) => (
      <Text color={r.isOverdue ? crm.danger : crm.text} fontWeight={r.isOverdue ? 600 : 400}>
        {fmtDate(r.date)}
      </Text>
    ),
  },
  ...(daysLabel
    ? [
        {
          header: daysLabel,
          render: (r: OpsAgendaItem) => (r.days ? <Pill bg="#fef2f2" color={crm.danger}>{r.days} d</Pill> : "—"),
        },
      ]
    : []),
  { header: "Responsável", render: (r) => <ResponsibleChip name={r.responsibleName} /> },
];

const AgendaTab = ({
  data,
  isMobile,
  range,
  onRange,
  consult,
  onConsult,
  open,
}: {
  data: ManagementOperations;
  isMobile: boolean;
  range: AgendaRange;
  onRange: (r: AgendaRange) => void;
  consult: AgendaConsult | "";
  onConsult: (c: AgendaConsult | "") => void;
  open: (id: string) => void;
}) => {
  const a = data.agenda;
  const counts = a.rangeCounts;
  const total = (r: AgendaRange) => counts[r].chegadas + counts[r].desembaracosPrevistos + counts[r].faturamentosEstimados;
  const rowKey = (r: OpsAgendaItem) => r.processId;

  return (
    <>
      <Flex justify="space-between" align="center" mb="16px" flexWrap="wrap" gap="10px">
        <SegmentedTabs<AgendaRange>
          value={range}
          onChange={onRange}
          items={[
            { key: "hoje", label: "Hoje", count: total("hoje") },
            { key: "semana", label: "Semana vigente", count: total("semana") },
            { key: "proxima", label: "Próxima semana", count: total("proxima") },
          ]}
        />
        <Text fontSize="sm" color={crm.textMuted}>
          {fmtDate(a.rangeStart)}
          {a.rangeStart !== a.rangeEnd ? ` a ${fmtDate(a.rangeEnd)}` : ""}
        </Text>
      </Flex>

      <Card title="Faturamento após a chegada · dias corridos desde a chegada, sem faturar">
        <Grid templateColumns={isMobile ? "1fr" : "repeat(2, 1fr)"} gap="16px">
          <Box>
            <Flex align="center" gap="8px" mb="8px">
              <Pill bg="#fffbeb" color={crm.warning}>11 a 15 dias</Pill>
              <Text fontSize="xs" color={crm.textMuted}>{a.billing11To15.length} processo(s)</Text>
            </Flex>
            <DataTable columns={agendaColumns("Chegada", "Dias")} rows={a.billing11To15} rowKey={rowKey} onRowClick={(r) => open(r.processId)} emptyText="Nenhum processo nessa faixa." initialPageSize={25} />
          </Box>
          <Box>
            <Flex align="center" gap="8px" mb="8px">
              <Pill bg="#fef2f2" color={crm.danger}>Mais de 15 dias</Pill>
              <Text fontSize="xs" color={crm.textMuted}>{a.billingOver15.length} processo(s)</Text>
            </Flex>
            <DataTable columns={agendaColumns("Chegada", "Dias")} rows={a.billingOver15} rowKey={rowKey} onRowClick={(r) => open(r.processId)} emptyText="Nenhum processo nessa faixa." initialPageSize={25} />
          </Box>
        </Grid>
      </Card>

      <Box h="16px" />
      <Grid templateColumns={isMobile ? "1fr" : "repeat(3, 1fr)"} gap="16px">
        <Card title={`Chegadas (${a.chegadas.length})`}>
          <DataTable columns={agendaColumns("Chegada")} rows={a.chegadas} rowKey={rowKey} onRowClick={(r) => open(r.processId)} emptyText="Sem chegadas no período." />
        </Card>
        <Card title={`Desembaraços previstos (${a.desembaracosPrevistos.length})`}>
          <DataTable columns={agendaColumns("Previsto", "Atraso")} rows={a.desembaracosPrevistos} rowKey={rowKey} onRowClick={(r) => open(r.processId)} emptyText="Sem desembaraços previstos no período." />
        </Card>
        <Card title={`Faturamentos estimados (${a.faturamentosEstimados.length})`}>
          <DataTable columns={agendaColumns("Estimado", "Atraso")} rows={a.faturamentosEstimados} rowKey={rowKey} onRowClick={(r) => open(r.processId)} emptyText="Sem faturamentos estimados no período." />
        </Card>
      </Grid>

      <Box h="16px" />
      <Card
        title="Consultar realizados"
        right={
          <SelectFilter
            label=""
            allLabel="Selecione…"
            value={consult}
            onChange={(v) => onConsult(v as AgendaConsult | "")}
            options={[
              { value: "faturadosHoje", label: `Faturados hoje (${a.consultCounts.faturadosHoje})` },
              { value: "faturadosOntem", label: `Faturados ontem (${a.consultCounts.faturadosOntem})` },
              { value: "desembaracados", label: `Desembaraços realizados no período (${a.consultCounts.desembaracados})` },
            ]}
          />
        }
      >
        {consult ? (
          <DataTable
            columns={agendaColumns(consult === "desembaracados" ? "Desembaraço" : "Faturamento")}
            rows={a.consult.items}
            rowKey={rowKey}
            onRowClick={(r) => open(r.processId)}
            emptyText="Nada encontrado."
            resetKey={consult}
          />
        ) : (
          <EmptyState>Escolha uma opção para listar os processos faturados ou desembaraçados.</EmptyState>
        )}
      </Card>
    </>
  );
};

// ------------------------------------------------------------------ Carteira

const CarteiraTab = ({
  data,
  isMobile,
  operation,
  onOperation,
  cambio,
  onCambio,
  open,
}: {
  data: ManagementOperations;
  isMobile: boolean;
  operation: string;
  onOperation: (v: string) => void;
  cambio: string;
  onCambio: (v: string) => void;
  open: (id: string) => void;
}) => {
  const p = data.portfolio;
  const k = p.kpis;
  const others = k.fobOutrasMoedas;
  const columns: Column<OpsPortfolioItem>[] = [
    { header: "Processo / cliente", render: (r) => <ProcessCell ref_={r.ref} client={r.clientName} urgent={r.isUrgent} /> },
    {
      header: "Etapa",
      render: (r) => (
        <Flex align="center" gap="6px">
          <Box w="8px" h="8px" borderRadius="full" bg={r.stageColor || crm.brand} flexShrink={0} />
          <Text>{r.stageName ?? "—"}</Text>
        </Flex>
      ),
    },
    { header: "Tempo do processo", render: (r) => (r.ageDays !== undefined ? `${r.ageDays} dias` : "—") },
    { header: "Valor FOB", render: (r) => fmtMoney(r.fobValue, r.fobCurrency) },
    {
      header: "Valor já pago",
      render: (r) => {
        const state = cambioLabels[r.cambioStatus];
        return (
          <Box>
            <Text>{r.paidOriginal > 0 ? fmtMoney(r.paidOriginal, r.fobCurrency) : "—"}</Text>
            {r.paidBrl > 0 && r.fobCurrency !== "BRL" && (
              <Text fontSize="11px" color={crm.textMuted}>≈ {fmtMoney(r.paidBrl, "BRL")}</Text>
            )}
            {r.paymentsWithoutRate > 0 && (
              <Text fontSize="11px" color={crm.warning}>{r.paymentsWithoutRate} pagto sem taxa</Text>
            )}
            <Pill bg={state.bg} color={state.fg}>{state.text}</Pill>
          </Box>
        );
      },
    },
    {
      header: "Invoice / BL",
      render: (r) => (
        <Box>
          <Text>{r.invoice || "—"}</Text>
          <Text fontSize="11px" color={crm.textMuted}>{r.bl || "—"}</Text>
        </Box>
      ),
    },
    {
      header: "Embarque / chegada",
      render: (r) => (
        <Box>
          <Text>{fmtDate(r.shipping)}</Text>
          <Text fontSize="11px" color={crm.textMuted}>{fmtDate(r.arrival)}</Text>
        </Box>
      ),
    },
    {
      header: "Rota / carga",
      render: (r) => (
        <Box>
          <Text>{[r.origin, r.destination].filter(Boolean).join(" → ") || "—"}</Text>
          <Text fontSize="11px" color={crm.textMuted}>{[r.transportMode, r.container].filter(Boolean).join(" · ") || "—"}</Text>
        </Box>
      ),
    },
    { header: "Responsável", render: (r) => <ResponsibleChip name={r.responsibleName} /> },
  ];

  return (
    <>
      <Flex gap="12px" flexWrap="wrap" mb="16px" align="flex-end">
        <SelectFilter
          label="Tipo da operação"
          value={operation}
          onChange={onOperation}
          options={[
            { value: "encomenda", label: "Encomenda" },
            { value: "proprios", label: "Próprios (distribuição)" },
          ]}
        />
        <SelectFilter
          label="Situação do câmbio"
          value={cambio}
          onChange={onCambio}
          options={[
            { value: "pago", label: "Pago" },
            { value: "aberto", label: "Em aberto" },
            { value: "naoinformado", label: "Não informado" },
          ]}
        />
        <Text fontSize="xs" color={crm.textMuted} pb="8px">
          Processos em {p.stageNames.length ? p.stageNames.join(", ") : "produção / navegando"} (etapas do Funil Gestão).
        </Text>
      </Flex>

      <Grid templateColumns={isMobile ? "1fr 1fr" : "repeat(4, 1fr)"} gap="16px" mb="16px">
        <KpiTile title="FOB total (USD)" value={fmtUsd(k.fobTotalUsd)} icon={<FaFileInvoiceDollar />} />
        <KpiTile title="FOB total (BRL)" value={fmtBrl(k.fobTotalBrl)} icon={<FaFileInvoiceDollar />} />
        <KpiTile
          title="FOB em outra moeda"
          value={others.length ? others.map((o) => fmtMoney(o.total, o.currency).replace(/,\d\d$/, "")).join(" · ") : "—"}
          hint={others.length ? others.map((o) => `${o.count} proc. ${o.currency}`).join(" · ") : undefined}
          icon={<FaFileInvoiceDollar />}
        />
        <KpiTile title="Já pago (USD)" value={fmtUsd(k.pagoUsd)} icon={<FaCheckCircle />} tone="success" />
        <KpiTile title="Já pago convertido (BRL)" value={fmtBrl(k.pagoConvertidoBrl)} icon={<FaCheckCircle />} tone="success" />
        <KpiTile title="Pagamentos sem taxa" value={k.pagamentosSemTaxa} icon={<FaExclamationTriangle />} tone={k.pagamentosSemTaxa > 0 ? "warning" : "default"} />
        <KpiTile title="Sem valor FOB" value={k.semValorFob} icon={<FaExclamationTriangle />} tone={k.semValorFob > 0 ? "warning" : "default"} />
        <KpiTile title="Processos na carteira" value={k.processos} icon={<FaBoxes />} />
      </Grid>

      <Card p="16px">
        <DataTable
          columns={columns}
          rows={p.items}
          rowKey={(r) => r.processId}
          onRowClick={(r) => open(r.processId)}
          emptyText="Nenhum processo na carteira com esses filtros."
          resetKey={`${operation}|${cambio}`}
        />
      </Card>
    </>
  );
};

// ------------------------------------------------------------------ Pendências

const PendenciasTab = ({ data, open }: { data: ManagementOperations; open: (id: string) => void }) => {
  const [type, setType] = useState<PendingType | "todos">("todos");
  const [sort, setSort] = useState<"atraso" | "responsavel">("atraso");
  const pendings = data.pendings;

  const rows = useMemo(() => {
    const list = pendings.items.filter((i) => type === "todos" || i.issues.some((x) => x.type === type));
    return [...list].sort((a, b) =>
      sort === "responsavel"
        ? (a.responsibleName ?? "zzz").localeCompare(b.responsibleName ?? "zzz") || b.maxDelayDays - a.maxDelayDays
        : b.maxDelayDays - a.maxDelayDays || a.ref.localeCompare(b.ref)
    );
  }, [pendings.items, type, sort]);

  const columns: Column<OpsPendingItem>[] = [
    { header: "Processo / cliente", render: (r) => <ProcessCell ref_={r.ref} client={r.clientName} /> },
    {
      header: "Pendências",
      render: (r) => (
        <Flex gap="6px" flexWrap="wrap">
          {r.issues
            .filter((i) => type === "todos" || i.type === type)
            .map((i, idx) => {
              const c = pendingColors[i.type];
              return (
                <Pill key={idx} bg={c.bg} color={c.fg}>
                  {i.label}
                  {i.daysLate ? ` · ${i.daysLate}d` : ""}
                </Pill>
              );
            })}
        </Flex>
      ),
    },
    { header: "Responsável", render: (r) => <ResponsibleChip name={r.responsibleName} /> },
    {
      header: "Maior atraso",
      render: (r) => (r.maxDelayDays > 0 ? <Text color={crm.danger} fontWeight="600">{r.maxDelayDays} dias</Text> : "—"),
    },
    {
      header: "Ação",
      render: (r) => (
        <Button size="xs" bg={crm.brand} color="white" _hover={{ bg: crm.brandDark }} onClick={(e) => { e.stopPropagation(); open(r.processId); }}>
          Abrir processo
        </Button>
      ),
    },
  ];

  return (
    <Card p="16px">
      <Flex justify="space-between" align="flex-end" mb="14px" flexWrap="wrap" gap="10px">
        <SegmentedTabs<PendingType | "todos">
          value={type}
          onChange={setType}
          items={[
            { key: "todos", label: "Todas", count: pendings.items.length },
            ...(Object.keys(pendingLabels) as PendingType[]).map((k) => ({
              key: k,
              label: pendingLabels[k],
              count: pendings.counts[k] ?? 0,
            })),
          ]}
        />
        <SelectFilter
          label="Ordenar por"
          allLabel="Maior atraso"
          value={sort === "responsavel" ? "responsavel" : ""}
          onChange={(v) => setSort(v === "responsavel" ? "responsavel" : "atraso")}
          options={[{ value: "responsavel", label: "Responsável" }]}
        />
      </Flex>
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(r) => r.processId}
        onRowClick={(r) => open(r.processId)}
        emptyText="Nenhuma pendência com esses filtros."
        resetKey={`${type}|${sort}`}
      />
      <Text fontSize="11px" color={crm.textFaint} mt="10px">
        Câmbio em aberto = parcela não paga vencida ou vencendo hoje. Dados faltantes consideram a etapa do processo (embarque, chegada, BL, DI).
      </Text>
    </Card>
  );
};

// ------------------------------------------------------------------ Consulta

const ConsultaTab = ({
  data,
  open,
  page,
  pageSize,
  onPage,
  onPageSize,
}: {
  data: ManagementOperations;
  open: (id: string) => void;
  page: number;
  pageSize: number;
  onPage: (p: number) => void;
  onPageSize: (s: number) => void;
}) => {
  const q = data.query;
  const columns: Column<OpsQueryItem>[] = [
    { header: "Processo", render: (r) => <Text fontWeight="700" color={crm.brand}>{r.ref || "—"}</Text> },
    { header: "Cliente", render: (r) => r.clientName ?? "—" },
    {
      header: "Status atual",
      render: (r) => (
        <Box>
          <Text>{r.status ?? "—"}</Text>
          {r.stageName && <Text fontSize="11px" color={crm.textMuted}>{r.stageName}</Text>}
        </Box>
      ),
    },
    {
      header: "Embarque / chegada",
      render: (r) => (
        <Box>
          <Text>{fmtDate(r.shipping)}</Text>
          <Text fontSize="11px" color={crm.textMuted}>{fmtDate(r.arrival)}</Text>
        </Box>
      ),
    },
    { header: "Origem → destino", render: (r) => [r.origin, r.destination].filter(Boolean).join(" → ") || "—" },
    {
      header: "DI / canal",
      render: (r) => (
        <Box>
          <Text>{r.di || "—"}</Text>
          {r.channel && <Text fontSize="11px" color={crm.textMuted}>{r.channel}</Text>}
        </Box>
      ),
    },
    { header: "Responsável", render: (r) => <ResponsibleChip name={r.responsibleName} /> },
  ];

  return (
    <Card p="16px">
      <DataTable
        columns={columns}
        rows={q.items}
        rowKey={(r) => r.processId}
        onRowClick={(r) => open(r.processId)}
        emptyText="Nenhum processo encontrado."
        server={{ page, pageSize, total: q.total, onPage, onPageSize }}
      />
    </Card>
  );
};

export default Operacoes;
